/**
 * V1 → V2 migration context fetcher.
 *
 * Asks classic (`takeover-app-classic`) whether the given V2 grid is
 * the destination of an active V1 migration. Used by the V2 board
 * page to decide whether to render `<V2MigrationBoardBanner>` above
 * the canvas.
 *
 * The migration toggle is admin-controlled and stored in classic's
 * `pool_metadata` table. We don't replicate the table here — the
 * single source of truth stays on classic, and V2 reads it back over
 * HTTP. This keeps the migration UX driven by one admin action (flip
 * the V1 toggle) rather than two (flip on V1 + edit a V2 env file).
 *
 * The endpoint is intentionally public on classic; see
 * `takeover-app-classic/src/app/api/pools/migration/lookup/route.ts`.
 */

const DEFAULT_CLASSIC_BASE_URL = "https://classic.takeover.fun";

/**
 * Per-request context surfaced by `getMigrationForGrid`. `untilMs` is
 * the unix-millis timestamp at which V1 yield stops flowing (T0). Pre-
 * and post-T0 are decided client-side from `Date.now()`; we intentionally
 * don't pre-decide on the server because Next caches the response for
 * 60s and a hard pre/post boundary at the cache key would flip stale
 * banners during the 60s window straddling T0.
 *
 * `v1TreasuryReclaimedAt` is the on-chain truth for "asset moved": it's
 * the unix-seconds timestamp the indexer wrote when the V1 TSFM
 * `TreasuryReclaimed` event fired (i.e. Phase 2 of the migration ran
 * and the Flaunch NFT moved out of the V1 yield path). It's `null` once
 * the field is being served but the rescue has not yet happened.
 *
 * Tri-state on the consumer side — important for staged deploys:
 *   - `number`    → rescue confirmed on-chain (post-asset, board live)
 *   - `null`      → field served by classic, rescue not yet confirmed
 *                   (pre-asset, board should be disabled)
 *   - `undefined` → field missing entirely, classic is still on the
 *                   pre-`v1TreasuryReclaimedAt` payload schema. Callers
 *                   must fall back to the time gate (`rewardsMigrated`
 *                   / `now >= untilMs`) so existing live migrations
 *                   (e.g. Noel Claw) don't regress to the disabled
 *                   pre-asset state during a deploy-order skew.
 *
 * The admin-set time gate (`untilMs` / `rewardsMigrated`) and the
 * on-chain rescue can drift ahead of or behind each other; on-chain
 * is the source of truth wherever both are available.
 */
export interface V2MigrationContext {
  v1PoolId: string;
  v1CoinAddress: string | null;
  v1CoinUrl: string;
  v1CoinSymbol: string;
  untilMs: number;
  rewardsMigrated: boolean;
  v1TreasuryReclaimedAt: number | null | undefined;
}

/**
 * Single source of truth for "is the V2 board live with yield?".
 *
 * Derives a clean pre/post-asset boolean from the tri-state
 * `v1TreasuryReclaimedAt` so the banner, the lobby gate, the
 * post-claim nudge, and the tile-click interceptor all agree on the
 * same answer. Splitting this off the consumers means a future signal
 * change (e.g. introducing per-grid yield-flow proof, or skipping the
 * indexer hop entirely) only has to land here.
 *
 * Resolution order:
 *   1. If the indexer-mirrored on-chain rescue timestamp is present
 *      (`number`), the V1 NFT has moved and V2 is post-asset.
 *   2. If the field is explicitly `null`, the rescue has NOT happened
 *      and V2 is pre-asset (board is disabled).
 *   3. If the field is `undefined`, classic hasn't shipped the new
 *      payload yet; fall back to the admin time gate so existing
 *      live migrations don't regress mid-deploy.
 *
 * `now` is injected so the banner can re-evaluate cheaply on its 30s
 * tick without re-deriving everything else from `Date.now()`.
 */
export function isMigrationAssetMoved(
  context: Pick<V2MigrationContext, "v1TreasuryReclaimedAt" | "rewardsMigrated" | "untilMs">,
  now: number = Date.now(),
): boolean {
  if (typeof context.v1TreasuryReclaimedAt === "number") return true;
  if (context.v1TreasuryReclaimedAt === null) return false;
  return context.rewardsMigrated || now >= context.untilMs;
}

interface ClassicLookupRow {
  v2Ugm: string;
  v2GridId: number;
  v1PoolId: string;
  v1CoinAddress: string | null;
  v1CoinUrl: string;
  v1CoinSymbol: string;
  untilMs: number;
  rewardsMigrated: boolean;
  v1TreasuryReclaimedAt?: number | null;
}

interface ClassicLookupEnvelope {
  data: ClassicLookupRow | null;
}

function getClassicBaseUrl(): string {
  // Default to production classic so the V2 staging build can talk
  // to real migration data without a per-environment override —
  // the lookup endpoint only returns rows for `migration_active =
  // TRUE`, so cross-env reads are safe.
  const raw = process.env.NEXT_PUBLIC_CLASSIC_BASE_URL || DEFAULT_CLASSIC_BASE_URL;
  return raw.replace(/\/$/, "");
}

/**
 * Look up the V1 migration row (if any) targeting `(ugm, gridId)`.
 *
 * Soft-fails to `null` on every error path:
 *   - bad URL / network error: classic outage shouldn't blank the V2
 *     board page; we just don't render the banner
 *   - non-2xx response: classic schema drift; same outcome
 *   - JSON parse error / unexpected shape: same outcome
 *
 * Hard-fail would let one classic deploy break every V2 board render.
 */
export async function getMigrationForGrid(
  ugm: string,
  gridId: number,
): Promise<V2MigrationContext | null> {
  const ugmLower = ugm.toLowerCase();
  if (!/^0x[a-f0-9]{40}$/.test(ugmLower)) return null;
  if (!Number.isFinite(gridId) || gridId < 0) return null;

  const base = getClassicBaseUrl();
  const url = `${base}/api/pools/migration/lookup?ugm=${ugmLower}&gridId=${gridId}`;

  let response: Response;
  try {
    response = await fetch(url, {
      // Next 14 fetch cache. 60s mirrors classic's `s-maxage`
      // header — if we revalidated more aggressively we'd just hit
      // a CDN cache miss for no UX gain.
      next: { revalidate: 60, tags: [`migration:${ugmLower}:${gridId}`] },
    });
  } catch {
    return null;
  }

  if (!response.ok) return null;

  let payload: ClassicLookupEnvelope;
  try {
    payload = (await response.json()) as ClassicLookupEnvelope;
  } catch {
    return null;
  }

  const row = payload?.data;
  if (!row) return null;

  // Defence against schema drift: if classic rolls a breaking change
  // and these fields aren't present, render nothing rather than a
  // broken banner with `undefined` interpolated into the copy.
  if (typeof row.untilMs !== "number" || typeof row.v1CoinSymbol !== "string") {
    return null;
  }

  // Pass the field through with its tri-state intact:
  //   - present-number  → rescue confirmed (post-asset)
  //   - present-null    → rescue not confirmed (pre-asset, disable board)
  //   - missing/undef   → stale classic schema; consumers must fall back
  //                       to the time gate so live migrations don't
  //                       regress mid-deploy.
  // We deliberately don't coerce `undefined → null` here — losing that
  // distinction is what would have flipped Noel Claw to the disabled
  // pre-asset board the moment V2 deployed ahead of classic.
  let v1TreasuryReclaimedAt: number | null | undefined;
  if (typeof row.v1TreasuryReclaimedAt === "number") {
    v1TreasuryReclaimedAt = row.v1TreasuryReclaimedAt;
  } else if (row.v1TreasuryReclaimedAt === null) {
    v1TreasuryReclaimedAt = null;
  } else {
    v1TreasuryReclaimedAt = undefined;
  }

  return {
    v1PoolId: row.v1PoolId,
    v1CoinAddress: row.v1CoinAddress,
    v1CoinUrl: row.v1CoinUrl,
    v1CoinSymbol: row.v1CoinSymbol,
    untilMs: row.untilMs,
    rewardsMigrated: Boolean(row.rewardsMigrated),
    v1TreasuryReclaimedAt,
  };
}
